'use client';

import React, { useState } from 'react';
import { ArrowLeft, MessageCircle, UserPlus, Users, CheckCheck, Reply, Check, X, BellOff,  } from 'lucide-react';

type NotifType = 'message' | 'contact_request' | 'group_invite';

interface Notification {
  id: string;
  type: NotifType;
  name: string;
  initials: string;
  avatarGradient: string;
  title: string;
  preview: string;
  timestamp: string;
  isRead: boolean;
  isOnline?: boolean;
}

const INITIAL_NOTIFICATIONS: Notification[] = [
  {
    id: 'n1',
    type: 'message',
    name: 'Ate Boni',
    initials: 'AB',
    avatarGradient: 'linear-gradient(135deg, #287FD8 0%, #6650D8 100%)',
    title: 'New message from Ate Boni',
    preview: 'Uy, kumusta ka na? Matagal na tayong hindi nag-usap!',
    timestamp: '2 min ago',
    isRead: false,
    isOnline: true,
  },
  {
    id: 'n2',
    type: 'contact_request',
    name: 'Carlo Mendoza',
    initials: 'CM',
    avatarGradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    title: 'Contact request',
    preview: 'Carlo Mendoza wants to connect with you',
    timestamp: '15 min ago',
    isRead: false,
  },
  {
    id: 'n3',
    type: 'group_invite',
    name: 'Dev Squad PH',
    initials: 'DS',
    avatarGradient: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
    title: 'Group invitation',
    preview: 'Marco Reyes invited you to join Dev Squad PH',
    timestamp: '1 hr ago',
    isRead: false,
  },
  {
    id: 'n4',
    type: 'message',
    name: 'Marco Reyes',
    initials: 'MR',
    avatarGradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    title: 'New message from Marco Reyes',
    preview: 'Sige, bukas na lang tayo mag-meet sa opisina.',
    timestamp: '2 hr ago',
    isRead: false,
    isOnline: true,
  },
  {
    id: 'n5',
    type: 'contact_request',
    name: 'Lea Villanueva',
    initials: 'LV',
    avatarGradient: 'linear-gradient(135deg, #EC4899 0%, #BE185D 100%)',
    title: 'Contact request',
    preview: 'Lea Villanueva wants to connect with you',
    timestamp: '3 hr ago',
    isRead: true,
  },
  {
    id: 'n6',
    type: 'group_invite',
    name: 'Barkada GC 2025',
    initials: 'BG',
    avatarGradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    title: 'Group invitation',
    preview: 'Jess invited you to join Barkada GC 2025',
    timestamp: 'Yesterday',
    isRead: true,
  },
  {
    id: 'n7',
    type: 'message',
    name: 'Ana Santos',
    initials: 'AS',
    avatarGradient: 'linear-gradient(135deg, #06B6D4 0%, #0284C7 100%)',
    title: 'New message from Ana Santos',
    preview: 'Kumain ka na ba? Hehe 😄',
    timestamp: 'Yesterday',
    isRead: true,
    isOnline: true,
  },
  {
    id: 'n8',
    type: 'group_invite',
    name: 'Work Announcements',
    initials: 'WA',
    avatarGradient: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
    title: 'Group invitation',
    preview: 'HR Team invited you to join Work Announcements',
    timestamp: 'Mon',
    isRead: true,
  },
];

interface NotificationsScreenProps {
  onBack: () => void;
  onOpenChat?: () => void;
}

type FilterTab = 'all' | 'messages' | 'requests' | 'invites';

export default function NotificationsScreen({ onBack, onOpenChat }: NotificationsScreenProps) {
  const [notifications, setNotifications] = useState<Notification[]>(INITIAL_NOTIFICATIONS);
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const filtered = notifications.filter(n => {
    if (dismissed.has(n.id)) return false;
    if (activeTab === 'messages') return n.type === 'message';
    if (activeTab === 'requests') return n.type === 'contact_request';
    if (activeTab === 'invites') return n.type === 'group_invite';
    return true;
  });

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, isRead: true } : n))
    );
  };

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const dismissNotif = (id: string) => {
    setDismissed(prev => new Set([...prev, id]));
  };

  const handleAccept = (id: string) => {
    markAsRead(id);
    dismissNotif(id);
  };

  const handleDecline = (id: string) => {
    dismissNotif(id);
  };

  const handleReply = (id: string) => {
    if (replyingTo === id) {
      setReplyingTo(null);
      setReplyText('');
    } else {
      markAsRead(id);
      setReplyingTo(id);
      setReplyText('');
    }
  };

  const sendReply = (id: string) => {
    if (!replyText.trim()) return;
    setReplyingTo(null);
    setReplyText('');
    dismissNotif(id);
    if (onOpenChat) onOpenChat();
  };

  const typeIcon = (type: NotifType) => {
    if (type === 'message') return <MessageCircle size={13} className="text-blue-400" />;
    if (type === 'contact_request') return <UserPlus size={13} className="text-amber-400" />;
    return <Users size={13} className="text-green-400" />;
  };

  const typeBadgeColor = (type: NotifType) => {
    if (type === 'message') return 'rgba(40,127,216,0.18)';
    if (type === 'contact_request') return 'rgba(245,158,11,0.18)';
    return 'rgba(34,197,94,0.18)';
  };

  const TABS: { key: FilterTab; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'messages', label: 'Messages' },
    { key: 'requests', label: 'Requests' },
    { key: 'invites', label: 'Invites' },
  ];

  return (
    <div
      style={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: '#101C29',
      }}
    >
      {/* Header */}
      <header
        className="flex items-center justify-between px-4 flex-shrink-0"
        style={{
          height: '64px',
          background: 'rgba(16,28,41,0.97)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="flex items-center justify-center w-[38px] h-[38px] rounded-full text-white/70 hover:bg-white/10 hover:text-white transition-all duration-150"
            aria-label="Back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <h1 className="text-[20px] font-bold text-white tracking-tight">Notifications</h1>
            {unreadCount > 0 && (
              <span
                className="flex items-center justify-center rounded-full text-white font-semibold"
                style={{
                  background: '#287FD8',
                  fontSize: '11px',
                  minWidth: '20px',
                  height: '20px',
                  padding: '0 5px',
                }}
              >
                {unreadCount}
              </span>
            )}
          </div>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[13px] font-medium text-blue-400 hover:bg-white/08 transition-all duration-150"
            style={{ background: 'rgba(40,127,216,0.12)' }}
          >
            <CheckCheck size={15} />
            Mark all read
          </button>
        )}
      </header>

      {/* Filter tabs */}
      <div
        className="flex items-center gap-1 px-4 py-3 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
      >
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className="px-3.5 py-1.5 rounded-xl text-[13px] font-medium transition-all duration-150"
            style={{
              background: activeTab === tab.key ? 'rgba(40,127,216,0.22)' : 'rgba(255,255,255,0.06)',
              color: activeTab === tab.key ? '#4A9EE8' : 'rgba(255,255,255,0.5)',
              border: activeTab === tab.key ? '1px solid rgba(40,127,216,0.35)' : '1px solid transparent',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-4 px-8">
            <div
              className="w-[72px] h-[72px] rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.06)' }}
            >
              <BellOff size={32} className="text-white/30" />
            </div>
            <p className="text-[15px] font-medium text-white/40 text-center">
              No notifications here
            </p>
          </div>
        ) : (
          filtered.map((notif, idx) => (
            <NotifRow
              key={notif.id}
              notif={notif}
              isLast={idx === filtered.length - 1}
              isReplying={replyingTo === notif.id}
              replyText={replyText}
              onReplyTextChange={setReplyText}
              onMarkRead={() => markAsRead(notif.id)}
              onReply={() => handleReply(notif.id)}
              onSendReply={() => sendReply(notif.id)}
              onAccept={() => handleAccept(notif.id)}
              onDecline={() => handleDecline(notif.id)}
              typeIcon={typeIcon(notif.type)}
              typeBadgeColor={typeBadgeColor(notif.type)}
            />
          ))
        )}
      </div>
    </div>
  );
}

interface NotifRowProps {
  notif: Notification;
  isLast: boolean;
  isReplying: boolean;
  replyText: string;
  onReplyTextChange: (v: string) => void;
  onMarkRead: () => void;
  onReply: () => void;
  onSendReply: () => void;
  onAccept: () => void;
  onDecline: () => void;
  typeIcon: React.ReactNode;
  typeBadgeColor: string;
}

function NotifRow({
  notif,
  isLast,
  isReplying,
  replyText,
  onReplyTextChange,
  onMarkRead,
  onReply,
  onSendReply,
  onAccept,
  onDecline,
  typeIcon,
  typeBadgeColor,
}: NotifRowProps) {
  return (
    <div
      style={{
        borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)',
        background: notif.isRead ? 'transparent' : 'rgba(40,127,216,0.05)',
        transition: 'background 0.2s',
      }}
    >
      <div className="flex items-start gap-3 px-4 py-3.5">
        {/* Avatar */}
        <div className="relative flex-shrink-0 mt-0.5">
          <div
            className="w-[50px] h-[50px] rounded-full flex items-center justify-center text-white font-bold text-[15px]"
            style={{ background: notif.avatarGradient }}
            aria-label={`${notif.name} avatar`}
          >
            {notif.initials}
          </div>
          {notif.isOnline && (
            <span
              className="absolute bottom-[1px] right-[1px] w-[12px] h-[12px] bg-green-400 rounded-full border-2"
              style={{ borderColor: '#101C29' }}
            />
          )}
          {/* Type badge */}
          <span
            className="absolute -bottom-1 -right-1 w-[20px] h-[20px] rounded-full flex items-center justify-center"
            style={{ background: typeBadgeColor, border: '1.5px solid #101C29' }}
          >
            {typeIcon}
          </span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-0.5">
            <span
              className="text-[14px] font-semibold leading-snug"
              style={{ color: notif.isRead ? 'rgba(255,255,255,0.75)' : '#FFFFFF' }}
            >
              {notif.title}
            </span>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {notif.timestamp}
              </span>
              {!notif.isRead && (
                <span
                  className="w-[8px] h-[8px] rounded-full flex-shrink-0"
                  style={{ background: '#287FD8' }}
                />
              )}
            </div>
          </div>
          <p
            className="text-[13px] leading-snug line-clamp-2"
            style={{ color: 'rgba(255,255,255,0.45)' }}
          >
            {notif.preview}
          </p>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-2.5">
            {notif.type === 'message' && (
              <>
                <button
                  onClick={onReply}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-medium transition-all duration-150"
                  style={{
                    background: isReplying ? 'rgba(40,127,216,0.25)' : 'rgba(40,127,216,0.14)',
                    color: '#4A9EE8',
                    border: isReplying ? '1px solid rgba(40,127,216,0.4)' : '1px solid transparent',
                  }}
                >
                  <Reply size={13} />
                  Quick Reply
                </button>
                {!notif.isRead && (
                  <button
                    onClick={onMarkRead}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-medium transition-all duration-150"
                    style={{
                      background: 'rgba(255,255,255,0.07)',
                      color: 'rgba(255,255,255,0.5)',
                    }}
                  >
                    <CheckCheck size={13} />
                    Mark read
                  </button>
                )}
              </>
            )}
            {(notif.type === 'contact_request' || notif.type === 'group_invite') && (
              <>
                <button
                  onClick={onAccept}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-medium transition-all duration-150"
                  style={{
                    background: 'rgba(34,197,94,0.14)',
                    color: '#4ADE80',
                  }}
                >
                  <Check size={13} />
                  Accept
                </button>
                <button
                  onClick={onDecline}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-medium transition-all duration-150"
                  style={{
                    background: 'rgba(239,68,68,0.12)',
                    color: '#F87171',
                  }}
                >
                  <X size={13} />
                  Decline
                </button>
              </>
            )}
          </div>

          {/* Inline reply box */}
          {isReplying && notif.type === 'message' && (
            <div
              className="mt-3 flex items-center gap-2 rounded-2xl px-3 py-2"
              style={{ background: 'rgba(255,255,255,0.07)' }}
            >
              <input
                autoFocus
                type="text"
                value={replyText}
                onChange={e => onReplyTextChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    onSendReply();
                  }
                }}
                placeholder="Type a reply…"
                className="flex-1 bg-transparent text-[13px] text-white placeholder-white/30 outline-none"
              />
              <button
                onClick={onSendReply}
                disabled={!replyText.trim()}
                className="flex items-center justify-center w-[30px] h-[30px] rounded-full transition-all duration-150"
                style={{
                  background: replyText.trim() ? '#287FD8' : 'rgba(255,255,255,0.1)',
                  color: replyText.trim() ? '#fff' : 'rgba(255,255,255,0.3)',
                }}
                aria-label="Send reply"
              >
                <Reply size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
